// Messaging Service - Direct Messages between users

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  arrayUnion,
  increment,
  writeBatch,
  Unsubscribe,
} from '@firebase/firestore';
import { db } from './firebase';
import type {
  Conversation,
  ConversationWithOther,
  Message,
  MessageCreateInput,
  MessageAttachment,
} from '../types/messaging';

const CONVERSATIONS_COLLECTION = 'conversations';
const MESSAGES_COLLECTION = 'messages';

/**
 * Get or create a conversation between two users
 */
export async function getOrCreateConversation(
  userId1: string,
  userId2: string,
  user1Info: { name: string; photoURL: string | null },
  user2Info: { name: string; photoURL: string | null }
): Promise<string> {
  // Sort IDs to ensure consistent document ID
  const sortedIds = [userId1, userId2].sort();
  const conversationId = `${sortedIds[0]}_${sortedIds[1]}`;

  const conversationRef = doc(db, CONVERSATIONS_COLLECTION, conversationId);
  const conversationDoc = await getDoc(conversationRef);

  if (conversationDoc.exists()) {
    // Update participant info in case it changed
    await updateDoc(conversationRef, {
      [`participantNames.${userId1}`]: user1Info.name,
      [`participantNames.${userId2}`]: user2Info.name,
      [`participantPhotos.${userId1}`]: user1Info.photoURL,
      [`participantPhotos.${userId2}`]: user2Info.photoURL,
    });
    return conversationId;
  }

  // Create new conversation
  const conversation: Omit<Conversation, 'id'> = {
    participantIds: sortedIds,
    participantNames: {
      [userId1]: user1Info.name,
      [userId2]: user2Info.name,
    },
    participantPhotos: {
      [userId1]: user1Info.photoURL,
      [userId2]: user2Info.photoURL,
    },
    lastMessage: {
      text: '',
      senderId: '',
      timestamp: serverTimestamp() as Timestamp,
    },
    unreadCount: {
      [userId1]: 0,
      [userId2]: 0,
    },
    createdAt: serverTimestamp() as Timestamp,
    updatedAt: serverTimestamp() as Timestamp,
    status: 'active',
  };

  await setDoc(conversationRef, conversation);
  return conversationId;
}

/**
 * Send a message in a conversation
 */
export async function sendMessage(
  conversationId: string,
  senderId: string,
  senderName: string,
  senderPhoto: string | null,
  input: MessageCreateInput
): Promise<string> {
  const batch = writeBatch(db);

  // Create message
  const messagesRef = collection(db, CONVERSATIONS_COLLECTION, conversationId, MESSAGES_COLLECTION);
  const messageRef = doc(messagesRef);

  const message: Omit<Message, 'id'> = {
    conversationId,
    senderId,
    senderName,
    senderPhoto: senderPhoto || undefined,
    text: input.text,
    createdAt: serverTimestamp() as Timestamp,
    readBy: [senderId],
    status: 'sent',
    attachments: input.attachments,
  };

  batch.set(messageRef, message);

  // Update conversation
  const conversationRef = doc(db, CONVERSATIONS_COLLECTION, conversationId);
  const conversationDoc = await getDoc(conversationRef);

  if (conversationDoc.exists()) {
    const conversation = conversationDoc.data() as Conversation;
    const otherUserId = conversation.participantIds.find((id) => id !== senderId);

    batch.update(conversationRef, {
      lastMessage: {
        text: input.text.substring(0, 100), // Truncate for preview
        senderId,
        timestamp: serverTimestamp(),
      },
      updatedAt: serverTimestamp(),
      ...(otherUserId ? { [`unreadCount.${otherUserId}`]: increment(1) } : {}),
    });
  }

  await batch.commit();
  return messageRef.id;
}

/**
 * Mark messages as read
 */
export async function markAsRead(conversationId: string, userId: string): Promise<void> {
  const batch = writeBatch(db);

  // Reset unread count
  const conversationRef = doc(db, CONVERSATIONS_COLLECTION, conversationId);
  batch.update(conversationRef, {
    [`unreadCount.${userId}`]: 0,
  });

  // Mark unread messages as read
  const messagesRef = collection(db, CONVERSATIONS_COLLECTION, conversationId, MESSAGES_COLLECTION);
  const unreadQuery = query(
    messagesRef,
    where('senderId', '!=', userId),
    orderBy('senderId'),
    orderBy('createdAt', 'desc'),
    limit(50)
  );

  const snapshot = await getDocs(unreadQuery);

  snapshot.docs.forEach((docSnap) => {
    const message = docSnap.data() as Message;
    if (!message.readBy.includes(userId)) {
      batch.update(docSnap.ref, {
        readBy: arrayUnion(userId),
        status: 'read',
      });
    }
  });

  await batch.commit();
}

/**
 * Get all conversations for a user
 */
export async function getUserConversations(userId: string): Promise<ConversationWithOther[]> {
  const q = query(
    collection(db, CONVERSATIONS_COLLECTION),
    where('participantIds', 'array-contains', userId),
    where('status', '==', 'active'),
    orderBy('updatedAt', 'desc')
  );

  const snapshot = await getDocs(q);

  return snapshot.docs.map((docSnap) => {
    const conversation = { id: docSnap.id, ...docSnap.data() } as Conversation;
    const otherUserId = conversation.participantIds.find((id) => id !== userId)!;

    return {
      ...conversation,
      otherUser: {
        id: otherUserId,
        name: conversation.participantNames[otherUserId] || 'Unbekannt',
        photoURL: conversation.participantPhotos[otherUserId] || null,
      },
    };
  });
}

/**
 * Subscribe to user's conversations (real-time)
 */
export function subscribeToConversations(
  userId: string,
  callback: (conversations: ConversationWithOther[]) => void
): Unsubscribe {
  const q = query(
    collection(db, CONVERSATIONS_COLLECTION),
    where('participantIds', 'array-contains', userId),
    where('status', '==', 'active'),
    orderBy('updatedAt', 'desc')
  );

  return onSnapshot(q, (snapshot) => {
    const conversations = snapshot.docs.map((docSnap) => {
      const conversation = { id: docSnap.id, ...docSnap.data() } as Conversation;
      const otherUserId = conversation.participantIds.find((id) => id !== userId)!;

      return {
        ...conversation,
        otherUser: {
          id: otherUserId,
          name: conversation.participantNames[otherUserId] || 'Unbekannt',
          photoURL: conversation.participantPhotos[otherUserId] || null,
        },
      };
    });

    callback(conversations);
  });
}

/**
 * Subscribe to messages in a conversation (real-time)
 */
export function subscribeToMessages(
  conversationId: string,
  callback: (messages: Message[]) => void,
  messageLimit = 50
): Unsubscribe {
  const q = query(
    collection(db, CONVERSATIONS_COLLECTION, conversationId, MESSAGES_COLLECTION),
    orderBy('createdAt', 'desc'),
    limit(messageLimit)
  );

  return onSnapshot(q, (snapshot) => {
    const messages = snapshot.docs
      .map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }))
      .reverse() as Message[];

    callback(messages);
  });
}

/**
 * Get messages in a conversation
 */
export async function getMessages(conversationId: string, messageLimit = 50): Promise<Message[]> {
  const q = query(
    collection(db, CONVERSATIONS_COLLECTION, conversationId, MESSAGES_COLLECTION),
    orderBy('createdAt', 'desc'),
    limit(messageLimit)
  );

  const snapshot = await getDocs(q);

  return snapshot.docs
    .map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    }))
    .reverse() as Message[];
}

/**
 * Get total unread count for a user across all conversations
 */
export async function getTotalUnreadCount(userId: string): Promise<number> {
  const q = query(
    collection(db, CONVERSATIONS_COLLECTION),
    where('participantIds', 'array-contains', userId),
    where('status', '==', 'active')
  );

  const snapshot = await getDocs(q);

  let total = 0;
  snapshot.docs.forEach((docSnap) => {
    const conversation = docSnap.data() as Conversation;
    total += conversation.unreadCount[userId] || 0;
  });

  return total;
}

/**
 * Subscribe to total unread count (real-time)
 */
export function subscribeToUnreadCount(
  userId: string,
  callback: (count: number) => void
): Unsubscribe {
  const q = query(
    collection(db, CONVERSATIONS_COLLECTION),
    where('participantIds', 'array-contains', userId),
    where('status', '==', 'active')
  );

  return onSnapshot(q, (snapshot) => {
    let total = 0;
    snapshot.docs.forEach((docSnap) => {
      const conversation = docSnap.data() as Conversation;
      total += conversation.unreadCount[userId] || 0;
    });

    callback(total);
  });
}

/**
 * Archive a conversation (hide from list but keep messages)
 */
export async function archiveConversation(conversationId: string): Promise<void> {
  const conversationRef = doc(db, CONVERSATIONS_COLLECTION, conversationId);
  await updateDoc(conversationRef, {
    status: 'archived',
    updatedAt: serverTimestamp(),
  });
}

/**
 * Block a conversation
 */
export async function blockConversation(conversationId: string): Promise<void> {
  const conversationRef = doc(db, CONVERSATIONS_COLLECTION, conversationId);
  await updateDoc(conversationRef, {
    status: 'blocked',
    updatedAt: serverTimestamp(),
  });
}

/**
 * Restore an archived conversation
 */
export async function restoreConversation(conversationId: string): Promise<void> {
  const conversationRef = doc(db, CONVERSATIONS_COLLECTION, conversationId);
  await updateDoc(conversationRef, {
    status: 'active',
    updatedAt: serverTimestamp(),
  });
}

/**
 * Check if a conversation exists between two users
 */
export async function getConversationBetweenUsers(
  userId1: string,
  userId2: string
): Promise<Conversation | null> {
  const sortedIds = [userId1, userId2].sort();
  const conversationId = `${sortedIds[0]}_${sortedIds[1]}`;

  const conversationRef = doc(db, CONVERSATIONS_COLLECTION, conversationId);
  const conversationDoc = await getDoc(conversationRef);

  if (!conversationDoc.exists()) {
    return null;
  }

  return { id: conversationDoc.id, ...conversationDoc.data() } as Conversation;
}
